import React, { useEffect, useState } from "react";
import { http, API } from "@/lib/api";
import { Star } from "@phosphor-icons/react";

/** RecentWorkGallery — public component. Pulls from /api/public/gallery and renders
 *  photo grid with category + city + month tags. No PII ever. */
export default function RecentWorkGallery({ limit = 12, compact = false, title = "Recent work" }) {
    const [items, setItems] = useState(null);

    useEffect(() => {
        http.get("/public/gallery")
            .then(({ data }) => setItems(data.items || []))
            .catch(() => setItems([]));
    }, []);

    if (!items) {
        return (
            <div className="text-slate-500 text-sm italic py-8 text-center">Loading recent work…</div>
        );
    }
    if (items.length === 0) {
        return null;
    }

    const shown = items.slice(0, limit);

    return (
        <section data-testid="recent-work-gallery" className={compact ? "py-8" : "py-16 sm:py-20"}>
            {!compact && (
                <div className="text-center mb-10">
                    <div className="label-eyebrow text-gold mb-3">/ {title}</div>
                    <h2 className="font-serif text-3xl sm:text-5xl">
                        Real homes. <span className="italic gold-text">Real Butlers.</span>
                    </h2>
                    <p className="text-slate-400 text-sm mt-4 max-w-xl mx-auto">
                        A sample of jobs we&apos;ve completed across Central Florida. Every photo is from a real client who rated us five stars.
                    </p>
                </div>
            )}
            <div className={`grid ${compact ? "grid-cols-2 md:grid-cols-4 gap-3" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"}`}>
                {shown.map((item) => (
                    <GalleryCard key={item.id} item={item} compact={compact} />
                ))}
            </div>
        </section>
    );
}

function GalleryCard({ item, compact }) {
    // Public photos are served without auth via /api/files/{path}/public? Actually the upload route returns storage paths.
    // For the gallery we render the first photo as a tile.
    const photo = item.photos?.[0];
    const src = photo ? `${API}/files/${photo}/public` : null;
    return (
        <div
            data-testid={`gallery-item-${item.id}`}
            className="group relative aspect-square overflow-hidden rounded-sm bg-slate-900 border border-white/10 hover:border-gold/60 transition"
        >
            {src ? (
                <img
                    src={src}
                    alt={item.category || "Project"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
            ) : (
                <div className="w-full h-full bg-slate-800" />
            )}
            {item.top_rated && (
                <div
                    data-testid={`gallery-top-rated-${item.id}`}
                    className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/90 backdrop-blur-sm shadow-lg"
                    title="Top-rated Butler"
                >
                    <span className="text-xs leading-none" aria-hidden>🏆</span>
                    <span className="text-[10px] uppercase tracking-widest font-medium text-slate-900">
                        Top
                    </span>
                </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className={`text-[10px] uppercase tracking-widest text-gold flex items-center gap-1 ${compact ? "" : "mb-1"}`}>
                    {item.featured && <Star size={10} weight="fill" />} {item.category}
                </div>
                {!compact && (
                    <div className="text-xs text-slate-300">
                        {item.city || "Central FL"}{item.month ? ` · ${item.month}` : ""}
                    </div>
                )}
            </div>
        </div>
    );
}
